import toast from 'react-hot-toast'
import { useRequest } from 'ahooks'

import useOTPInput from '$actions/useOTPInput'
import useTOTPBlock from '$actions/useTOTPBlock'

import { asyncSuccessResolver } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'

type UseAuthenticatorTOTPOptions = FactorHookOptions

export const useAuthenticatorTOTP = ({
  preferred_mfa_token_type = 'ott',
  apiInstance: { mfaChallengeAuthenticatorTOTPVerify },
  onVerified
}: UseAuthenticatorTOTPOptions) => {
  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      reset()
    }
  }

  const verifyReq = useRequest(
    asyncSuccessResolver(mfaChallengeAuthenticatorTOTPVerify, successResolver),
    {
      manual: true,
      onError: onApiError,
      onSuccess: res => onVerified(res.data.mfa_token)
    }
  )

  const requests = [verifyReq]

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)
  const disabled = isAnyLoading

  const otpInput = useOTPInput({
    disabled,
    onFilled: (code, resetInput) => {
      const { runAsync, cancel } = verifyReq

      cancel()

      toast
        .promise(runAsync({ code, type: preferred_mfa_token_type }), {
          error: errorMessageResolver,
          loading: 'Verifying..',
          success: 'Verified.'
        })
        .finally(resetInput)
    }
  })

  const totpBlock = useTOTPBlock({
    otpInput,
    title: 'TOTP Verification',
    description: 'Get the code from your authenticator app.'
  })

  const reset = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
    otpInput.resetPasswords()
  }

  return {
    isAnyLoading,
    reset,
    totpBlock
  }
}

export default useAuthenticatorTOTP
